import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const TABS = ['Events', 'My Attendance'];

export default function MemberEvents() {
  const [tab, setTab] = useState(0);
  const [events, setEvents] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regLoading, setRegLoading] = useState(null);
  const [search, setSearch] = useState('');

  const fetchEvents = useCallback(() => {
    setLoading(true);
    api.get('/events/list/')
      .then(res => setEvents(res.data.results || res.data))
      .catch(() => toast.error('Failed to load events'))
      .finally(() => setLoading(false));
  }, []);

  const fetchAttendances = useCallback(() => {
    setLoading(true);
    api.get('/events/my-attendances/')
      .then(res => setAttendances(res.data.results || res.data))
      .catch(() => toast.error('Failed to load attendances'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 0) fetchEvents();
    else fetchAttendances();
  }, [tab, fetchEvents, fetchAttendances]);

  const handleRegister = async (eventId) => {
    setRegLoading(eventId);
    try {
      await api.post(`/events/${eventId}/register/`);
      toast.success('Registered successfully!');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Events</h1>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${tab === i ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : (
        <>
          {/* TAB 0: Events List */}
          {tab === 0 && (
            events.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-500 mb-2">No Events</h2>
                <p className="text-gray-400">There are no events available at the moment.</p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {events.filter(ev => !search || ev.title.toLowerCase().includes(search.toLowerCase()) || (ev.venue || '').toLowerCase().includes(search.toLowerCase()) || (ev.category_display || '').toLowerCase().includes(search.toLowerCase())).map((ev, idx) => (
                  <div key={ev.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition relative">
                    <div className="absolute top-2 left-2 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center z-10">{idx + 1}</div>
                    {ev.cover_image && (
                      <div className="h-40 bg-gray-50">
                        <img src={ev.cover_image} alt={ev.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800 truncate">{ev.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ev.status === 'upcoming' ? 'bg-blue-100 text-blue-700' : ev.status === 'ongoing' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {ev.status_display || ev.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        {ev.category_display && <p><span className="font-medium text-gray-600">Category:</span> {ev.category_display}</p>}
                        <p><span className="font-medium text-gray-600">Date:</span> {ev.date_from}{ev.date_to !== ev.date_from ? ` — ${ev.date_to}` : ''}</p>
                        {ev.venue && <p><span className="font-medium text-gray-600">Venue:</span> {ev.venue}</p>}
                        {ev.is_virtual && ev.meeting_link && <p><span className="font-medium text-gray-600">Type:</span> Virtual</p>}
                        <p><span className="font-medium text-gray-600">Registered:</span> {ev.registered_count}{ev.max_participants ? ` / ${ev.max_participants}` : ''}</p>
                        {parseFloat(ev.fee) > 0 && <p><span className="font-medium text-gray-600">Fee:</span> ₦{parseFloat(ev.fee).toLocaleString()}</p>}
                      </div>
                      {ev.status !== 'completed' && !ev.is_full && (
                        <button onClick={() => handleRegister(ev.id)} disabled={regLoading === ev.id}
                          className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition disabled:opacity-50">
                          {regLoading === ev.id ? (
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                          )}
                          Register
                        </button>
                      )}
                      {ev.is_full && ev.status !== 'completed' && (
                        <p className="text-center text-xs text-red-500 font-semibold mt-3">Event Full</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* TAB 1: My Attendance */}
          {tab === 1 && (
            attendances.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-500 mb-2">No Event Registrations</h2>
                <p className="text-gray-400">You haven't registered for any events yet.</p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {attendances.filter(a => !search || a.event_title.toLowerCase().includes(search.toLowerCase())).map((a, idx) => (
                  <div key={a.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3 hover:shadow-md transition">
                    <div className="flex items-center gap-2">
                      <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                      <h3 className="font-semibold text-gray-800">{a.event_title}</h3>
                    </div>
                    <div className="text-xs text-gray-500">
                      <p>Registered: {new Date(a.registered_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${a.attendance_marked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {a.attendance_marked ? 'Present' : 'Not Marked'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${a.certificate_issued ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {a.certificate_issued ? 'Certificate Issued' : 'No Certificate'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
